import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ChevronLeft, ChevronDown, BarChart, GitBranch, Building2, Map, FileText, Upload, Loader2, CheckCircle2, XCircle, Send, Trash2, Bot, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Diagram } from '@/types/diagram';
import type { DiagramPayload } from '@/services/diagramChatService';
import { useApp } from '@/contexts/AppContext';
import { useProject } from '@/contexts/ProjectContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { threatcatService, type ImportedDiagramResult, type ParsedDiagram } from '@/services/threatcatService';
import { draftDiagramService, type DraftDiagramOut } from '@/services/draftDiagramService';
import { queryKeys } from '@/lib/queryKeys';
import { normalizeData } from '@/lib/parsers';
import Tm7ResolveDialog from '@/components/threatcat/Tm7ResolveDialog';
import DraftDiagramCard from './diagrams/DraftDiagramCard';
import OverviewTab from './OverviewTab';
import ConnectionsTab from './ConnectionsTab';
import EntitiesTab from './EntitiesTab';
import BoundariesTab from './BoundariesTab';
import DiagramTab from './DiagramTab';
import DiagramNotesTab from './diagrams/DiagramNotesTab';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { toast } from 'sonner';
import ConnectModal from '@/components/threatcat/ConnectModal';
import { ThreatCatAuthError } from '@/lib/errors';

type SubTab = 'overview' | 'connections' | 'entities' | 'boundaries' | 'diagram' | 'notes';

const SUB_TABS: { value: SubTab; label: string; icon: React.ElementType }[] = [
  { value: 'overview',     label: 'Overview',     icon: BarChart   },
  { value: 'connections',  label: 'Connections',  icon: GitBranch  },
  { value: 'entities',     label: 'Entities',     icon: Building2  },
  { value: 'boundaries',   label: 'Boundaries',   icon: Map        },
  { value: 'diagram',      label: 'Diagram',      icon: Map        },
  { value: 'notes',        label: 'Notes',        icon: FileText   },
];

interface DiagramsTabProps {
  view: 'list' | 'detail';
  setView: (v: 'list' | 'detail') => void;
  subTab: string;
  setSubTab: (t: string) => void;
}

export default function DiagramsTab({ view, setView, subTab, setSubTab }: DiagramsTabProps) {
  const { diagrams, currentDiagramIndex, currentDiagram, setCurrentDiagram, setAppState, notes } = useApp();
  const { cookie, disconnect } = useThreatCat();
  const { projectId: pid, detail } = useProject();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const reviewIndex = useReviewCommentsIndex();
  const effectiveVersionId = detail?.project_version_id ?? null;
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  type ImportStatus = 'idle' | 'parsing' | 'importing' | { results: ImportedDiagramResult[] } | { error: string };
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle');

  const { data: draftDiagrams = [], isSuccess: draftQueryResolved } = useQuery({
    queryKey: queryKeys.draftDiagrams.all(pid!),
    queryFn: () => draftDiagramService.list(pid!),
    enabled: !!pid && !isNaN(pid),
  });

  const [importingDraftId, setImportingDraftId] = useState<number | null>(null);
  const [activeDraft, setActiveDraft] = useState<DraftDiagramOut | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [pendingImportId, setPendingImportId] = useState<number | null>(null);
  const [pendingTm7Diagrams, setPendingTm7Diagrams] = useState<ParsedDiagram[] | null>(null);
  const prevDiagramIndexRef = useRef<number>(0);

  const canEdit = !!pid && detail?.permission !== 'read_only';

  const draftToFakeDiagram = useCallback((draft: DraftDiagramOut): Diagram => {
    const p = draft.payload;
    return {
      id: -(draft.id),
      name: draft.name,
      description: p.description || draft.description || '',
      diagram_schema_version: p.diagram_schema_version ?? 1,
      entities: (p.entities ?? []) as Diagram['entities'],
      boundaries: (p.boundaries ?? []) as Diagram['boundaries'],
      paths: (p.paths ?? []) as Diagram['paths'],
    };
  }, []);

  const handleSelectDraft = useCallback((draft: DraftDiagramOut) => {
    prevDiagramIndexRef.current = currentDiagramIndex;
    const fakeDiagram = draftToFakeDiagram(draft);
    setAppState({ currentDiagram: fakeDiagram });
    setActiveDraft(draft);
    setSubTab('overview');
    setView('detail');
  }, [currentDiagramIndex, draftToFakeDiagram, setAppState, setSubTab, setView]);

  const handleLeaveDraft = useCallback(() => {
    setActiveDraft(null);
    if (diagrams.length > 0) {
      const idx = Math.min(prevDiagramIndexRef.current, diagrams.length - 1);
      setCurrentDiagram(idx);
    }
    setView('list');
  }, [diagrams, setCurrentDiagram, setView]);

  // Recover activeDraft after tab-switch remount AND reconcile with fresh data.
  // Radix TabsContent unmounts inactive children, so local activeDraft state
  // resets to null while diagramView in MainLayout stays 'detail'.
  //
  // (A) Recovery: when activeDraft is null and we detect a fake-draft
  //     currentDiagram (negative id), restore activeDraft from the query cache.
  //     Wait for the query to resolve before deciding — an unresolved query
  //     returns [] which would incorrectly trigger a fallback to list view.
  //
  // (B) Reconciliation: once activeDraft is set, keep checking fresh query
  //     results so a draft removed elsewhere (e.g. imported/discarded in
  //     another session) doesn't remain active indefinitely.
  useEffect(() => {
    if (view !== 'detail') return;

    // (A) Recovery: activeDraft lost due to remount
    if (!activeDraft && currentDiagram && currentDiagram.id < 0) {
      if (!draftQueryResolved) return; // keep detail view while query is pending
      const draftId = -(currentDiagram.id);
      const recovered = draftDiagrams.find((d) => d.id === draftId);
      if (recovered) {
        setActiveDraft(recovered);
      } else {
        setView('list');
      }
      return;
    }

    // (B) Reconciliation: verify active draft still exists in fresh data
    if (activeDraft && draftQueryResolved) {
      const stillExists = draftDiagrams.some((d) => d.id === activeDraft.id);
      if (!stillExists) {
        handleLeaveDraft();
      }
    }
  }, [view, activeDraft, currentDiagram, draftDiagrams, draftQueryResolved, setView, handleLeaveDraft]);

  const handleDraftUpdated = useCallback((updatedDraft: DraftDiagramOut) => {
    setActiveDraft(updatedDraft);
    if (pid) queryClient.setQueryData<DraftDiagramOut[]>(queryKeys.draftDiagrams.all(pid), (prev) =>
      prev?.map((d) => (d.id === updatedDraft.id ? updatedDraft : d)),
    );
  }, [pid, queryClient]);

  const handleDiagramChatUpdate = useCallback((updatedPayload: DiagramPayload) => {
    if (!currentDiagram) return;
    const updated: Diagram = {
      ...currentDiagram,
      entities: updatedPayload.entities,
      boundaries: updatedPayload.boundaries,
      paths: updatedPayload.paths,
    };
    setAppState({ currentDiagram: updated });

    if (activeDraft) {
      setActiveDraft((prev) => prev ? {
        ...prev,
        payload: { ...prev.payload, ...updatedPayload },
      } : prev);
    }
  }, [currentDiagram, setAppState, activeDraft]);

  const doImport = useCallback(async (diagramsToImport: ParsedDiagram[]) => {
    if (isWriteBackLocked) {
      if (writeBackLockMessage) setImportStatus({ error: writeBackLockMessage });
      return;
    }
    if (!cookie || !effectiveVersionId) {
      setPendingTm7Diagrams(diagramsToImport);
      setConnectOpen(true);
      return;
    }
    setImportStatus('importing');
    try {
      const { imported } = await threatcatService.importDiagrams(
        diagramsToImport,
        String(effectiveVersionId),
        cookie,
      );
      setImportStatus({ results: imported });
      // Refresh project data so newly imported diagrams appear immediately
      if (imported.some((r) => r.ok)) {
        const exportData = await threatcatService.fetchThreatCatProjectExport(
          cookie,
          effectiveVersionId,
          pid ?? undefined,
        );
        setAppState(normalizeData(exportData));
      }
    } catch (err) {
      if (err instanceof ThreatCatAuthError) disconnect();
      setImportStatus({ error: err instanceof Error ? err.message : 'Import failed' });
    }
  }, [cookie, disconnect, effectiveVersionId, isWriteBackLocked, pid, setAppState, writeBackLockMessage]);

  const handleImportDraft = useCallback(async (id: number) => {
    if (isWriteBackLocked) {
      if (writeBackLockMessage) toast.error(writeBackLockMessage);
      return;
    }
    if (!cookie || !effectiveVersionId) {
      setPendingImportId(id);
      setConnectOpen(true);
      return;
    }
    setImportingDraftId(id);
    try {
      const result = await draftDiagramService.importToThreatCat(
        id,
        cookie,
        String(effectiveVersionId),
      );
      if (pid) queryClient.setQueryData<DraftDiagramOut[]>(queryKeys.draftDiagrams.all(pid), (prev) => prev?.filter((d) => d.id !== id));
      toast.success(`"${result.name}" imported into ThreatCat`);
      if (activeDraft?.id === id) {
        setActiveDraft(null);
      }
      const exportData = await threatcatService.fetchThreatCatProjectExport(
        cookie,
        effectiveVersionId,
        pid ?? undefined,
      );
      setAppState(normalizeData(exportData));
      if (activeDraft?.id === id) {
        setView('list');
      }
    } catch (err) {
      if (err instanceof ThreatCatAuthError) disconnect();
      toast.error(err instanceof Error ? err.message : 'Failed to import diagram');
    } finally {
      setImportingDraftId(null);
    }
  }, [activeDraft, cookie, disconnect, effectiveVersionId, isWriteBackLocked, pid, queryClient, setAppState, setView, writeBackLockMessage]);

  const handleConnected = () => {
    setConnectOpen(false);
    if (isWriteBackLocked) {
      setPendingImportId(null);
      setPendingTm7Diagrams(null);
      return;
    }
    if (pendingImportId != null) {
      const id = pendingImportId;
      setPendingImportId(null);
      void handleImportDraft(id);
    } else if (pendingTm7Diagrams) {
      const diagrams = pendingTm7Diagrams;
      setPendingTm7Diagrams(null);
      void doImport(diagrams);
    }
  };

  const connectModal = (
    <ConnectModal
      open={connectOpen}
      onClose={() => {
        setConnectOpen(false);
        setPendingImportId(null);
        setPendingTm7Diagrams(null);
      }}
      onConnected={handleConnected}
    />
  );

  const handleDiscardDraft = useCallback(async (id: number) => {
    try {
      await draftDiagramService.remove(id);
      if (pid) queryClient.setQueryData<DraftDiagramOut[]>(queryKeys.draftDiagrams.all(pid), (prev) => prev?.filter((d) => d.id !== id));
      toast.success('Draft diagram discarded');
      if (activeDraft?.id === id) {
        handleLeaveDraft();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to discard draft diagram');
    }
  }, [activeDraft, handleLeaveDraft]);

  // Parsed diagrams waiting for unresolved path resolution
  const [pendingDiagrams, setPendingDiagrams] = useState<ParsedDiagram[] | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleSelectDiagram = (index: number) => {
    setCurrentDiagram(index);
    setSubTab('overview');
    setView('detail');
  };

  const handleTm7Change = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !detail?.project_version_id || isWriteBackLocked) return;
    e.target.value = '';
    setImportStatus('parsing');
    try {
      const { diagrams: parsed } = await threatcatService.parseTm7(file);
      const hasUnresolved = parsed.some((d) => d.unresolved_paths.length > 0);
      if (hasUnresolved) {
        // Show resolve dialog so user can assign missing endpoints
        setPendingDiagrams(parsed);
        setResolveDialogOpen(true);
        setImportStatus('idle');
      } else {
        await doImport(parsed);
      }
    } catch (err) {
      setImportStatus({ error: err instanceof Error ? err.message : 'Parse failed' });
    }
  };

  const handleResolveImport = async (resolved: ParsedDiagram[]) => {
    setResolveDialogOpen(false);
    setPendingDiagrams(null);
    await doImport(resolved);
  };

  const handleResolveCancel = () => {
    setResolveDialogOpen(false);
    setPendingDiagrams(null);
  };

  // ── Shared TM7 import widget ───────────────────────────────────────────────

  const isLoading = importStatus === 'parsing' || importStatus === 'importing';
  const loadingLabel = importStatus === 'parsing' ? 'Parsing…' : 'Importing…';

  const tm7ImportWidget = detail?.project_version_id ? (
    <div className="space-y-2">
      <div className="flex items-center justify-end">
        <span title={writeBackLockMessage ?? undefined} className="inline-flex">
          <button
            type="button"
            disabled={isLoading || isWriteBackLocked}
            title={writeBackLockMessage ?? undefined}
            onClick={() => {
              if (isWriteBackLocked) return;
              fileInputRef.current?.click();
            }}
            className="flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-accent/50 hover:border-primary/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading
              ? <><Loader2 className="h-3 w-3 animate-spin" /> {loadingLabel}</>
              : <><Upload className="h-3 w-3" /> Import .tm7</>}
          </button>
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".tm7"
          className="hidden"
          onChange={handleTm7Change}
        />
      </div>
      {typeof importStatus === 'object' && (
        <div className="rounded-md border p-3 text-xs space-y-1">
          {'error' in importStatus ? (
            <p className="flex items-center gap-1.5 text-destructive">
              <XCircle className="h-3.5 w-3.5 shrink-0" />
              {importStatus.error}
            </p>
          ) : (
            importStatus.results.map((r) => (
              <p key={r.name} className={cn('flex items-center gap-1.5', r.ok ? 'text-green-600 dark:text-green-400' : 'text-destructive')}>
                {r.ok
                  ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  : <XCircle className="h-3.5 w-3.5 shrink-0" />}
                <span className="font-medium">{r.name}</span>
                {r.error && <span className="text-muted-foreground">— {r.error}</span>}
              </p>
            ))
          )}
          <button
            type="button"
            onClick={() => setImportStatus('idle')}
            className="mt-1 text-muted-foreground hover:text-foreground underline"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  ) : null;

  // ── Resolve dialog (rendered unconditionally — it's a portal) ────────────

  const resolveDialog = pendingDiagrams && (
    <Tm7ResolveDialog
      open={resolveDialogOpen}
      diagrams={pendingDiagrams}
      onImport={handleResolveImport}
      onCancel={handleResolveCancel}
      importing={importStatus === 'importing'}
    />
  );

  // ── List view ──────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <>
        {resolveDialog}
        {connectModal}
        <div className="space-y-3">
          {tm7ImportWidget}

          {/* Draft diagrams pending import */}
          {draftDiagrams.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pending Import</p>
              {draftDiagrams.map((draft) => (
                <DraftDiagramCard
                  key={draft.id}
                  draft={draft}
                  canEdit={canEdit}
                  onSelect={handleSelectDraft}
                  onImport={handleImportDraft}
                  onDiscard={handleDiscardDraft}
                  isImporting={importingDraftId === draft.id}
                  importDisabledReason={writeBackLockMessage}
                />
              ))}
            </div>
          )}

          {/* Diagram list */}
          {diagrams.map((diagram, index) => {
            const reviewCounts = reviewIndex.getDiagramCounts(diagram.id);
            return (
              <button
                key={diagram.id ?? index}
                type="button"
                onClick={() => handleSelectDiagram(index)}
                className="w-full text-left flex items-center justify-between rounded-lg border bg-card px-4 py-3 hover:bg-accent/50 hover:border-primary/40 transition-colors group"
              >
                <div>
                  <p className="font-medium text-sm">{diagram.name || `Diagram ${index + 1}`}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {diagram.entities?.length ?? 0} entities · {diagram.paths?.length ?? 0} connections
                    {diagram.boundaries?.length ? ` · ${diagram.boundaries.length} boundaries` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {reviewCounts.total > 0 && (
                    <span
                      data-diagram-review-count={diagram.id}
                      className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300"
                    >
                      <MessageSquare className="h-3 w-3" />
                      {reviewCounts.total}
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      </>
    );
  }

  // ── Detail view ────────────────────────────────────────────────────────────

  return (
    <>
      {resolveDialog}
      {connectModal}
      <div className="space-y-4">

      {/* Top bar — different for draft vs normal diagram */}
      {activeDraft ? (
        /* ── Draft header ─────────────────────────────────────────────── */
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={handleLeaveDraft}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
              All diagrams
            </button>
            <span className="text-muted-foreground/50 shrink-0">/</span>
            {/* Quick-jump dropdown for switching diagrams while viewing a draft */}
            {diagrams.length > 0 ? (
              <div ref={dropdownRef} className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="flex items-center gap-1 font-medium text-sm text-foreground hover:text-muted-foreground transition-colors truncate"
                >
                  {activeDraft.name}
                  <ChevronDown className="h-3 w-3 shrink-0" />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 z-50 min-w-48 rounded-md border bg-popover shadow-md py-1">
                    {diagrams.map((d, i) => (
                      <button
                        key={d.id ?? i}
                        type="button"
                        onClick={() => {
                          handleLeaveDraft();
                          setCurrentDiagram(i);
                          setSubTab('overview');
                          setView('detail');
                          setDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                      >
                        {d.name || `Diagram ${i + 1}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <span className="font-medium text-sm truncate">{activeDraft.name}</span>
            )}
            <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">
              Draft
            </span>
            <span className="inline-flex items-center gap-0.5 rounded-md bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-inset ring-slate-600/10 dark:bg-slate-900/30 dark:text-slate-400 shrink-0">
              <Bot className="h-2.5 w-2.5" />
              Agent
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => handleDiscardDraft(activeDraft.id)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Discard
            </Button>
            <span title={writeBackLockMessage ?? undefined} className="inline-flex">
              <Button
                size="sm"
                title={writeBackLockMessage ?? undefined}
                onClick={() => handleImportDraft(activeDraft.id)}
                disabled={importingDraftId === activeDraft.id || isWriteBackLocked}
              >
                {importingDraftId === activeDraft.id
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Send className="h-3.5 w-3.5 mr-1" />}
                Import to ThreatCat
              </Button>
            </span>
          </div>
        </div>
      ) : (
        /* ── Normal diagram header ────────────────────────────────────── */
        <div className="flex items-start justify-between gap-2">
          {/* Left: back / quick-jump — shown when there are multiple diagrams or drafts */}
          {diagrams.length > 1 || draftDiagrams.length > 0 ? (
            <div className="flex items-center gap-1 text-sm">
              <button
                type="button"
                onClick={() => setView('list')}
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                All diagrams
              </button>
              {diagrams.length > 1 && (
                <>
                  <span className="text-muted-foreground/50">/</span>
                  {/* Quick-jump dropdown */}
                  <div ref={dropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setDropdownOpen((o) => !o)}
                      className="flex items-center gap-1 font-medium text-foreground hover:text-muted-foreground transition-colors"
                    >
                      {currentDiagram?.name || `Diagram ${currentDiagramIndex + 1}`}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 z-50 min-w-48 rounded-md border bg-popover shadow-md py-1">
                        {diagrams.map((d, i) => (
                          <button
                            key={d.id ?? i}
                            type="button"
                            onClick={() => {
                              setCurrentDiagram(i);
                              setSubTab('overview');
                              setDropdownOpen(false);
                            }}
                            className={cn(
                              'w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors',
                              i === currentDiagramIndex && 'bg-accent/50 font-medium'
                            )}
                          >
                            {d.name || `Diagram ${i + 1}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div /> /* spacer so import button stays right-aligned */
          )}

          {/* Right: TM7 import widget */}
          {tm7ImportWidget}
        </div>
      )}

      {/* Sub-tab nav */}
      {(() => {
        const currentCounts = reviewIndex.getDiagramCounts(currentDiagram?.id);
        const subTabCounts: Partial<Record<SubTab, number>> = {
          overview: currentCounts.overview,
          connections: currentCounts.connections,
          entities: currentCounts.entities,
          boundaries: currentCounts.boundaries,
          notes: currentCounts.notes,
        };
        return (
          <div className="overflow-x-auto overflow-y-hidden border-b -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="flex items-stretch gap-0 w-max sm:w-auto">
              {SUB_TABS.map(({ value, label, icon: Icon }) => {
                const count = subTabCounts[value] ?? 0;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSubTab(value)}
                    className={cn(
                      'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap',
                      subTab === value
                        ? 'border-primary text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{label}</span>
                    {count > 0 && (
                      <span
                        data-subtab-review-count={value}
                        className="inline-flex items-center justify-center rounded-full bg-amber-500/15 px-1.5 py-0.5 text-xs font-semibold text-amber-800 dark:text-amber-300"
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Sub-tab content */}
      <div className="space-y-4">
        <ErrorBoundary>
          {subTab === 'overview'    && <OverviewTab />}
          {subTab === 'connections' && <ConnectionsTab draftId={activeDraft?.id} onDraftUpdated={handleDraftUpdated} />}
          {subTab === 'entities'    && <EntitiesTab draftId={activeDraft?.id} onDraftUpdated={handleDraftUpdated} />}
          {subTab === 'boundaries'  && <BoundariesTab draftId={activeDraft?.id} onDraftUpdated={handleDraftUpdated} />}
          {subTab === 'diagram'     && <DiagramTab draftId={activeDraft?.id} onDiagramUpdated={activeDraft ? handleDiagramChatUpdate : undefined} />}
          {subTab === 'notes'       && <DiagramNotesTab notes={notes.filter((n) => currentDiagram != null && n.diagram_id === currentDiagram.id)} />}
        </ErrorBoundary>
      </div>
      </div>
    </>
  );
}
